import { useRef, useState, useEffect } from "react";
import { Plus, CheckCircle2, Loader2, Send, Paperclip, Camera, Mic, X, Store, Cpu, ArrowRight, ArrowLeft, Pencil, Trash2, PanelLeftClose, PanelLeft, PanelRight, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AddToProjectButton from "@/components/projects/AddToProjectButton";

const API = process.env.REACT_APP_BACKEND_URL;
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const money = (n) => "$" + (n >= 1e6 ? (n / 1e6).toFixed(2) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : Number(n).toFixed(2));

// ── Feature 1: persistent, collapsible task history + rename + cleanup ──────
export function TaskSidebar({ tasks, activeId, onSelect, onNew, sessionStats, collapsed, onToggle, onRename, onDelete, onClearAll, swarm }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");

  if (collapsed) {
    return (
      <div className="flex-shrink-0 w-[56px] h-full flex flex-col items-center border-r border-white/[0.08] bg-[#070B14]/80 backdrop-blur-xl py-3 gap-2" data-testid="task-sidebar-collapsed">
        <button onClick={onToggle} className="p-2 text-slate-400 hover:text-[#14F0C8]" data-testid="sidebar-expand-btn" title="Expand"><PanelLeft className="w-5 h-5" /></button>
        <button onClick={onNew} className="p-2 text-slate-400 hover:text-[#14F0C8]" data-testid="new-query-collapsed" title="New Query"><Plus className="w-5 h-5" /></button>
        <div className="flex-1 overflow-y-auto flex flex-col items-center gap-2 mt-2">
          {tasks.slice(0, 12).map((t) => (
            <button key={t.id} onClick={() => onSelect(t.id)} title={t.label || t.prompt}
              className={`w-8 h-8 rounded-lg flex items-center justify-center border ${t.id === activeId ? "border-[#14F0C8] bg-[#14F0C8]/10" : "border-white/[0.08]"}`}>
              {t.status === "active" ? <Loader2 className="w-3.5 h-3.5 text-[#14F0C8] animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 w-[280px] h-full flex flex-col border-r border-white/[0.08] bg-[#070B14]/80 backdrop-blur-xl" data-testid="task-sidebar">
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">Task History</div>
        <div className="flex items-center gap-1">
          {tasks.length > 0 && <button onClick={onClearAll} className="text-[10px] text-slate-500 hover:text-red-400 transition-colors" data-testid="clear-history-btn">Clear</button>}
          <button onClick={onToggle} className="p-1 text-slate-400 hover:text-[#14F0C8]" data-testid="sidebar-collapse-btn" title="Collapse"><PanelLeftClose className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {tasks.length === 0 && <div className="text-xs text-slate-600 px-1 py-4 text-center">No queries yet. Ask the swarm below.</div>}
        {tasks.map((t) => (
          <div key={t.id} className={`group rounded-xl border p-3 transition-all ${t.id === activeId ? "border-[#14F0C8] bg-[#14F0C8]/[0.06] shadow-[0_0_20px_rgba(20,240,200,0.15)]" : "border-white/[0.07] bg-white/[0.02] hover:border-white/20"}`} data-testid={`task-card-${t.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] mb-1">
              {t.status === "active" ? <Loader2 className="w-3 h-3 text-[#14F0C8] animate-spin" /> : <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
              <span className={t.status === "active" ? "text-[#14F0C8]" : "text-emerald-400"}>{t.status === "active" ? "Active" : "Completed"}</span>
              <span className="text-slate-600 ml-auto">{t.time}</span>
              {t.status !== "active" && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {swarm && <AddToProjectButton swarm={swarm} runId={t.id} taskTitle={t.label || t.prompt || ""} accent="#14F0C8" compact />}
                  <button onClick={(e) => { e.stopPropagation(); setEditing(t.id); setDraft(t.label || t.prompt); }} data-testid={`task-rename-${t.id}`} title="Rename"><Pencil className="w-3 h-3 text-slate-400 hover:text-[#14F0C8]" /></button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(t.id); }} data-testid={`task-delete-${t.id}`} title="Delete"><Trash2 className="w-3 h-3 text-slate-400 hover:text-red-400" /></button>
                </div>
              )}
            </div>
            {editing === t.id ? (
              <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
                onBlur={() => { onRename(t.id, draft.trim() || t.prompt); setEditing(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { onRename(t.id, draft.trim() || t.prompt); setEditing(null); } if (e.key === "Escape") setEditing(null); }}
                className="w-full bg-[#0F1629] border border-[#14F0C8]/40 rounded px-2 py-1 text-xs text-slate-100 outline-none" data-testid={`task-rename-input-${t.id}`} />
            ) : (
              <button onClick={() => onSelect(t.id)} className="w-full text-left">
                <div className="text-xs text-slate-200 line-clamp-2">{t.label || t.prompt}</div>
                {t.uplift != null && <div className="text-[11px] font-bold text-[#14F0C8] mt-1" style={{ fontFamily: MONO }}>{money(t.uplift)} ↑</div>}
              </button>
            )}
          </div>
        ))}
      </div>
      <button onClick={onNew} data-testid="new-query-btn"
        className="mx-3 mb-2 flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg border border-white/[0.1] hover:border-[#14F0C8]/60 text-slate-200 transition-colors">
        <Plus className="w-3.5 h-3.5" /> New Query
      </button>
      <div className="mx-3 mb-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-3" data-testid="session-stats">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Session Stats</div>
        <div className="text-[11px] text-slate-300 space-y-0.5" style={{ fontFamily: MONO }}>
          <div className="flex justify-between"><span className="text-slate-400">Queries</span><span>{sessionStats.queries}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Total cost</span><span>${sessionStats.cost.toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Value found</span><span className="text-[#14F0C8]">{money(sessionStats.value)}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Agents deployed</span><span>{sessionStats.agents.toLocaleString()}</span></div>
        </div>
      </div>
    </div>
  );
}

// ── Full-screen chat transcript (shown after a run completes) ────────────────
export function FullChat({ messages, onOpenDoc, onOpenPanel }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [messages]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-y-auto px-4 py-6" data-testid="full-chat">
      {onOpenPanel && (
        <button onClick={onOpenPanel} data-testid="open-side-panel-btn"
          className="absolute top-4 right-4 z-10 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/[0.12] bg-[#0B1120]/85 backdrop-blur hover:border-[#14F0C8]/60 text-slate-200 transition-colors">
          <PanelRight className="w-3.5 h-3.5 text-[#14F0C8]" /> Side panel
        </button>
      )}
      <div className="max-w-3xl mx-auto space-y-4">
        {messages.map((m, i) => {
          if (m.kind === "doc") {
            return (
              <div key={i} className="flex justify-start">
                <div className="w-7 h-7 rounded-lg bg-[#14F0C8]/15 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0"><Cpu className="w-3.5 h-3.5 text-[#14F0C8]" /></div>
                <button onClick={() => onOpenDoc(m.runId)} data-testid={`doc-card-${m.runId}`}
                  className="text-left rounded-2xl border border-[#14F0C8]/30 bg-[#14F0C8]/[0.05] hover:border-[#14F0C8] transition-colors p-4 max-w-[80%] w-full group">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#14F0C8]" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-100">{m.title}</div>
                      <div className="text-[11px] text-slate-400">Quantum optimization report · click to open full screen</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#14F0C8] group-hover:translate-x-1 transition-all" />
                  </div>
                  {m.uplift != null && <div className="mt-2 text-lg font-bold text-[#14F0C8]" style={{ fontFamily: MONO }}>{money(m.uplift)} uplift identified</div>}
                </button>
              </div>
            );
          }
          return (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <div className="w-7 h-7 rounded-lg bg-[#14F0C8]/15 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0"><Cpu className="w-3.5 h-3.5 text-[#14F0C8]" /></div>}
              <div className={`max-w-[78%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#0ea5e9]/20 border border-[#0ea5e9]/30 text-slate-100" : "bg-white/[0.04] border border-white/[0.08] text-slate-200"}`} data-testid={`chat-bubble-${m.role}`}>
                {m.pending ? <span className="flex items-center gap-2 text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</span> : m.text}
                {m.attachment && <div className="mt-1 text-[10px] text-slate-400">📎 {m.attachment}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Feature 2: conversational multimodal chat dock ──────────────────────────
export function ChatDock({ messages, prompt, setPrompt, onSend, onFollowup, running, chips,
                          onPickChip, attached, onAttach, onClearAttach, hasResult, showTranscript = true, mode = "retail" }) {
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [intent, setIntent] = useState("followup");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Auto-grow the input as typed/transcribed text streams in (up to ~4 lines, then scroll).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 104) + "px";
  }, [prompt]);

  // Voice-to-prompt via MediaRecorder + server-side Whisper (native Web Speech API is unreliable)
  const toggleVoice = async () => {
    if (recording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm",
      });
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short. Please speak longer."); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const res = await fetch(`${API}/api/swarms/transcribe`, { method: "POST", credentials: "include", body: fd });
          if (res.ok) {
            const data = await res.json();
            if (data.text) { setPrompt((prev) => (prev ? prev + " " + data.text : data.text)); toast.success("Transcription complete"); }
            else toast.error("No speech detected. Try again.");
          } else {
            const err = await res.json().catch(() => ({}));
            toast.error(err.detail || "Transcription failed");
          }
        } catch (err) {
          toast.error("Transcription failed: " + (err.message || "Network error"));
        }
        setTranscribing(false);
      };
      mediaRecorderRef.current = mr;
      mr.start(250);
      setRecording(true);
      toast.success("Listening… tap mic to stop");
    } catch (err) {
      if (err.name === "NotAllowedError") toast.error("Microphone permission denied. Enable it in your browser settings.");
      else if (err.name === "NotFoundError") toast.error("No microphone found.");
      else toast.error("Could not access microphone: " + (err.message || "Unknown error"));
    }
  };

  const submit = () => {
    if (!prompt.trim() || running) return;
    if (hasResult && intent === "followup") { onFollowup(prompt); }
    else { onSend(prompt); setIntent("followup"); }
  };

  return (
    <div className="flex-shrink-0 border-t border-white/[0.08] bg-[#070B14]/92 backdrop-blur-2xl z-20" data-testid="chat-dock">
      {showTranscript && messages.length > 0 && (
        <div ref={scrollRef} className="max-h-[240px] overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
          <div className="max-w-3xl mx-auto space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="w-7 h-7 rounded-lg bg-[#14F0C8]/15 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <Cpu className="w-3.5 h-3.5 text-[#14F0C8]" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#0ea5e9]/20 border border-[#0ea5e9]/30 text-slate-100" : "bg-white/[0.04] border border-white/[0.08] text-slate-200"}`}
                  data-testid={`chat-bubble-${m.role}`}>
                  {m.pending ? <span className="flex items-center gap-2 text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</span> : m.text}
                  {m.attachment && <div className="mt-1 text-[10px] text-slate-400">📎 {m.attachment}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-4 py-3">
        <div className="max-w-3xl mx-auto">
          {!running && !hasResult && chips?.length > 0 && (
            <div className="flex gap-2 justify-center mb-2 flex-wrap" data-testid="prompt-chips">
              {chips.map((s) => (
                <button key={s} onClick={() => onPickChip(s)}
                  className="text-[11px] text-slate-400 hover:text-[#14F0C8] border border-white/[0.08] bg-white/[0.03] rounded-full px-3 py-1 transition-colors">{s}</button>
              ))}
            </div>
          )}
          {hasResult && !running && (
            <div className="flex gap-1 justify-center mb-2" data-testid="chat-intent-toggle">
              {[["followup", "Ask follow-up"], ["new", "New query"]].map(([k, l]) => (
                <button key={k} onClick={() => setIntent(k)}
                  className={`text-[11px] px-3 py-1 rounded-full border transition-colors ${intent === k ? "border-[#14F0C8] bg-[#14F0C8]/10 text-[#14F0C8]" : "border-white/[0.08] text-slate-400 hover:text-slate-200"}`}
                  data-testid={`intent-${k}`}>{l}</button>
              ))}
            </div>
          )}
          {attached && (
            <div className="flex items-center gap-2 mb-2 w-fit rounded-lg border border-white/[0.12] bg-white/[0.04] px-3 py-1.5 text-xs" data-testid="attachment-chip">
              <Paperclip className="w-3 h-3 text-[#14F0C8]" />
              <span className="text-slate-200">{attached.filename}</span>
              <span className="text-slate-500">({attached.kind}, {attached.chars} chars)</span>
              <button onClick={onClearAttach} data-testid="clear-attachment"><X className="w-3 h-3 text-slate-400 hover:text-white" /></button>
            </div>
          )}
          <div className="flex items-end gap-2 rounded-2xl border border-white/[0.1] bg-[#0F1629]/90 pl-3 pr-2 py-2 shadow-2xl" data-testid="prompt-bar">
            <button onClick={() => fileRef.current?.click()} className="p-1.5 text-slate-400 hover:text-[#14F0C8] transition-colors" data-testid="attach-file-btn" title="Attach file">
              <Paperclip className="w-[18px] h-[18px]" />
            </button>
            <button onClick={() => imgRef.current?.click()} className="p-1.5 text-slate-400 hover:text-[#14F0C8] transition-colors" data-testid="attach-image-btn" title="Attach image">
              <Camera className="w-[18px] h-[18px]" />
            </button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.csv,.xlsx" data-testid="file-input"
              onChange={(e) => e.target.files[0] && onAttach(e.target.files[0])} />
            <input ref={imgRef} type="file" hidden accept="image/*" data-testid="image-input"
              onChange={(e) => e.target.files[0] && onAttach(e.target.files[0])} />
            <textarea ref={taRef} value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={1}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder={hasResult && intent === "followup"
                ? (mode === "jnj" ? "Ask a follow-up, e.g. Which trial sites should we prioritize first?" : "Ask a follow-up, e.g. Which stores should I prioritize first?")
                : (mode === "jnj" ? "Ask the swarm, e.g. Identify cold chain routing inefficiencies across our biologics portfolio" : "Ask the swarm, e.g. Optimize size curves for our Southeast region")}
              className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-500 resize-none py-1.5 max-h-[104px] overflow-y-auto leading-5"
              data-testid="prompt-input" disabled={running} />
            <button onClick={toggleVoice} disabled={transcribing} className={`relative p-1.5 transition-colors ${recording ? "text-red-400" : transcribing ? "text-[#14F0C8]" : "text-slate-400 hover:text-[#14F0C8]"}`} data-testid="voice-btn" title={recording ? "Stop recording" : transcribing ? "Transcribing…" : "Voice input"}>
              {transcribing ? <Loader2 className="w-[18px] h-[18px] animate-spin" /> : <Mic className="w-[18px] h-[18px]" />}
              {recording && <span className="absolute inset-0 m-auto w-2 h-2 rounded-full bg-red-500 animate-ping" />}
            </button>
            <button onClick={submit} disabled={running}
              className="w-9 h-9 rounded-xl bg-[#0ea5e9] text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50"
              data-testid="prompt-submit">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : (hasResult && intent === "followup") ? <ArrowRight className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Feature 4: rich agent hover tooltip ─────────────────────────────────────
export function AgentTooltip({ agent, x, y, stageW }) {
  const left = Math.min(x + 18, (stageW || 900) - 420);
  return (
    <div className="absolute z-40 pointer-events-none rounded-xl border bg-[#0B1120]/96 backdrop-blur-xl shadow-2xl overflow-hidden"
      style={{ left, top: Math.max(12, y - 40), width: 400, borderColor: agent.color }} data-testid="agent-tooltip">
      <div style={{ background: agent.color, height: 3 }} />
      <div className="p-4">
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm text-slate-100" style={{ fontFamily: MONO }}>{agent.id}</span>
          <span className="text-slate-500">|</span>
          <span className="text-sm text-slate-200 uppercase tracking-wide">{agent.role}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">Division {agent.division}</div>
        <Row label="TASK" body={agent.task} />
        {agent.output_summary && <Row label={agent.status === "completed" ? "OUTPUT SUMMARY" : "STATUS"} body={agent.output_summary} />}
        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]" style={{ fontFamily: MONO }}>
          <Metric label="Status" value={agent.status === "completed" ? "✓ Completed" : "Idle"} />
          <Metric label="Confidence" value={`${agent.confidence}%`} />
          <Metric label="Duration" value={`${agent.duration_s}s`} />
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Model routing</div>
          <div className="text-[12px] text-slate-200">{agent.model_label} <span className="text-slate-500">(routed by QNNEAL)</span></div>
          <div className="text-[10px] text-slate-500">{agent.routing_reason}</div>
          <div className="text-[10px] text-emerald-400 mt-0.5" style={{ fontFamily: MONO }}>Cost ${agent.cost?.toFixed(4)} · {agent.savings_percent}% saved vs GPT-4o</div>
        </div>
        {agent.data_sources?.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Data sources</div>
            <ul className="text-[11px] text-slate-400 list-disc list-inside">
              {agent.data_sources.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}
        <div className="mt-3 grid grid-cols-1 gap-1 text-[10px] text-slate-500">
          <div><span className="text-slate-600">Receives from:</span> {agent.receives_from}</div>
          <div><span className="text-slate-600">Feeds into:</span> {agent.feeds_into}</div>
        </div>
      </div>
    </div>
  );
}
const Row = ({ label, body }) => (
  <div className="mt-2.5"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div><div className="text-[12px] text-slate-300 leading-snug">{body}</div></div>
);
const Metric = ({ label, value }) => (
  <div className="rounded bg-white/[0.03] px-2 py-1"><div className="text-[9px] text-slate-500 uppercase">{label}</div><div className="text-slate-200">{value}</div></div>
);

// ── Feature 6: execution-specific store panel ───────────────────────────────
export function StorePanel({ cards, meta, filter, setFilter, onReload, onClose }) {
  const [openCard, setOpenCard] = useState(null);
  const set = (k, v) => { const f = { ...filter, [k]: v }; setFilter(f); onReload(f); };
  return (
    <div className="fixed inset-y-0 right-0 w-[62%] max-w-[820px] bg-[#0B111F]/98 backdrop-blur-xl border-l border-[#14F0C8]/30 shadow-2xl z-50 flex flex-col" data-testid="store-panel">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2 font-bold"><Store className="w-5 h-5 text-[#14F0C8]" /> Store-Level Action Cards</div>
        <button onClick={onClose} data-testid="store-close-btn" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="px-6 py-3 border-b border-slate-800 flex flex-wrap gap-2">
        <input placeholder="Search store / city…" data-testid="store-search" value={filter.q}
          onChange={(e) => set("q", e.target.value)}
          className="flex-1 min-w-[160px] bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" />
        {[["region", meta.regions], ["priority", ["HIGH", "MEDIUM", "LOW"]], ["brand", ["Men's Wearhouse", "Jos. A. Bank"]]].map(([key, opts]) => (
          <select key={key} value={filter[key]} data-testid={`store-filter-${key}`} onChange={(e) => set(key, e.target.value)}
            className="bg-slate-800/60 border border-slate-700 rounded-lg px-2 py-1.5 text-xs outline-none capitalize">
            <option value="">{key}: all</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {cards.map((c) => (
          <div key={c.store_id} className="rounded-xl border border-slate-700/70 bg-slate-800/40 overflow-hidden" data-testid={`store-card-${c.store_id}`}>
            <button onClick={() => setOpenCard(openCard === c.store_id ? null : c.store_id)} className="w-full text-left p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-sm">Store #{c.store_id} — {c.brand}, {c.name}</div>
                  <div className="text-[11px] text-slate-400">{c.address}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{c.region} · {c.district}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.priority === "HIGH" ? "bg-red-500/20 text-red-300" : c.priority === "MEDIUM" ? "bg-amber-500/20 text-amber-300" : "bg-slate-600/30 text-slate-300"}`}>{c.priority}</span>
              </div>
              <div className="text-emerald-400 text-sm font-bold mt-1" style={{ fontFamily: MONO }}>+{money(c.impact_usd)}/quarter</div>
            </button>
            {openCard === c.store_id && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-700/60 pt-3" data-testid={`store-detail-${c.store_id}`}>
                {c.actions.map((a, ai) => (
                  <div key={ai} className="rounded-lg bg-black/20 border border-white/[0.06] p-3">
                    <div className="text-xs font-semibold text-[#14F0C8]">{a.type}</div>
                    <div className="text-[12px] text-slate-200 mt-0.5">{a.summary}</div>
                    {a.destination && (
                      <div className="mt-2 rounded-lg bg-white/[0.03] border border-white/[0.06] p-2.5 text-[11px] text-slate-300" style={{ fontFamily: MONO }}>
                        <div className="text-slate-400">DESTINATION: {a.destination.name} (#{a.destination.store_id})</div>
                        <div>{a.destination.address}</div>
                        <div>Distance: {a.destination.distance_mi} mi · {a.destination.drive_min} min drive</div>
                        <div>Dest stock: {a.destination.current_stock} units · stockout in {a.destination.stockout_days} days</div>
                        <div>Method: {a.destination.method} · cost ${a.destination.cost}</div>
                        <div className="text-emerald-400">Net impact: +{money(a.destination.net_impact)}</div>
                        {a.alternatives?.length > 0 && <div className="text-slate-500 mt-1">Also: {a.alternatives.map((x) => `${x.name} (${x.distance_mi}mi, ${x.note})`).join(" · ")}</div>}
                      </div>
                    )}
                    {a.source_dc && (
                      <div className="mt-2 rounded-lg bg-white/[0.03] border border-white/[0.06] p-2.5 text-[11px] text-slate-300" style={{ fontFamily: MONO }}>
                        <div className="text-slate-400">SOURCE: {a.source_dc.name}</div>
                        <div>{a.source_dc.address} · {a.source_dc.units_available} units available</div>
                        <div>Method: {a.source_dc.method} · {a.source_dc.delivery_window} · cost ${a.source_dc.cost}</div>
                        <div className="text-emerald-400">Expected rental revenue: +{money(a.source_dc.expected_revenue)}</div>
                      </div>
                    )}
                    {a.size_rows && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-white/[0.06]" style={{ fontFamily: MONO }}>
                        <div className="grid grid-cols-4 gap-1 px-2 py-1 text-[10px] text-slate-500 bg-white/[0.03]"><span>Size</span><span className="text-right">Current</span><span className="text-right">Recommended</span><span className="text-right">Δ</span></div>
                        {a.size_rows.map((r) => (
                          <div key={r.size} className="grid grid-cols-4 gap-1 px-2 py-1 text-[11px] text-slate-300 border-t border-white/[0.04]">
                            <span>{r.size}</span><span className="text-right">{r.current}</span><span className="text-right">{r.recommended}</span>
                            <span className={`text-right ${r.delta_pct > 0 ? "text-emerald-400" : r.delta_pct < 0 ? "text-red-400" : "text-slate-500"}`}>{r.delta_pct > 0 ? "+" : ""}{r.delta_pct}%</span>
                          </div>
                        ))}
                        <div className="px-2 py-1 text-[10px] text-slate-500 border-t border-white/[0.04]">Basis: {a.basis}</div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Execution checklist</div>
                  {c.checklist.map((ck, ci) => (
                    <div key={ci} className="flex items-center gap-2 text-[11px] text-slate-300 py-0.5">
                      <span className="w-3.5 h-3.5 rounded border border-slate-600" />
                      <span>{ck.task}</span><span className="text-slate-500 ml-auto">{ck.owner}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500">Confidence {c.confidence}% · data {c.data_freshness}</div>
              </div>
            )}
          </div>
        ))}
        <div className="text-center text-xs text-slate-500 py-2">Showing {cards.length} enriched store action cards</div>
      </div>
    </div>
  );
}
